/**
 * Utility functions for working with fund calendars and reporting periods
 */

/**
 * Gets a human-readable description of a fund calendar interval
 * @param intervalType The interval type string from the fund calendar
 * @returns Human-readable description
 */
export function getIntervalDescription(intervalType: string): string {
  const descriptions: Record<string, string> = {
    eow: "Weekly - Reports end of each week",
    eom: "Monthly - Reports end of each month",
    eoq: "Quarterly - Reports end of each quarter",
    eoy: "Yearly - Reports end of each year",
  };

  return descriptions[intervalType] || intervalType;
}

/**
 * Calculates the next report end date based on the calendar interval type
 * @param startDate The starting date
 * @param intervalType The interval type from the fund calendar
 * @returns Date object representing the next end date
 */
export function calculateNextEndDate(
  startDate: Date,
  intervalType: string
): Date {
  const result = new Date(startDate);

  switch (intervalType) {
    case "eow":
      result.setDate(result.getDate() + 7);
      break;
    case "eom":
      // Move to the last day of the current month
      result.setMonth(result.getMonth() + 1);
      result.setDate(0);
      break;
    case "eoq":
      result.setMonth(result.getMonth() + 3);
      break;
    case "eoy":
      result.setFullYear(result.getFullYear() + 1);
      break;
    default:
      // Default to monthly
      result.setMonth(result.getMonth() + 1);
  }

  return result;
}

/**
 * Generates a sequence of future reporting period dates based on a calendar's interval definition
 * @param startDate The base date to start from
 * @param intervalType The interval type from the fund calendar
 * @param count Number of dates to generate
 * @returns Array of dates representing future reporting periods
 */
export function generateFuturePeriods(
  startDate: Date,
  intervalType: string,
  count: number
): Date[] {
  const result: Date[] = [];
  let currentDate = new Date(startDate);

  for (let i = 0; i < count; i++) {
    currentDate = calculateNextEndDate(currentDate, intervalType);
    result.push(new Date(currentDate));
  }

  return result;
}

/**
 * Returns an array of predefined interval options for fund calendars
 * @returns Array of interval options with values and labels
 */
export function getIntervalOptions(): { value: string; label: string }[] {
  return [
    { value: "eow", label: "Weekly" },
    { value: "eom", label: "Monthly" },
    { value: "eoq", label: "Quarterly" },
    { value: "eoy", label: "Yearly" },
  ];
}
